import { zodResolver } from '@hookform/resolvers/zod';
import {
  TextInput,
  Button,
  RadioGroup,
} from '@porterin/component-library/src/base-components';
import GoogleMapsAutoComplete from '@porterin/component-library/src/composite-components/google-auto-complete/GoogleAutoComplete';
import { useLocaleContext } from '@porterin/component-library/src/contexts/locale/LocaleContext';
import { LocaleProviderWrapper } from '@porterin/component-library/src/providers/LocaleProviderWrapper';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React from 'react';
import { useForm } from 'react-hook-form';

import { FareEstimateFormLabel } from '../domain/configs/Store.store';
import { CityContextType } from '../domain/context/CityContext';
import { CityContextProvider } from '../domain/context/CityProvider';
import { useFareEstimateContent } from '../domain/context/FareEstimateContentContext';
import { AuthBinding } from '../domain/entities/AuthBinding';

import {
  submitFareEstimateFormData,
  stringToFormType,
  prefillAuthData,
  getInitialValues,
  getIsBookingFlowEnabled,
} from './FareEstimateHelper';
import { FormData, makeSchema } from './FareEstimateValidationSchema';
import CallbackConsentCheckbox from './CallbackConsentCheckbox';

interface FareEstimateFormProps {
  category: string;
  city_context?: CityContextType;
  auth_data?: AuthBinding;
  on_lead_success_handler: (show: boolean) => void;
}

const FareEstimateFormCmp: React.FC<FareEstimateFormProps> = ({
  category,
  city_context,
  auth_data,
  on_lead_success_handler,
}) => {
  return (
    <LocaleProviderWrapper>
      <FareEstimateFormContent
        category={category}
        city_context={city_context}
        auth_data={auth_data}
        on_lead_success_handler={on_lead_success_handler}
      />
    </LocaleProviderWrapper>
  );
};

const FareEstimateFormContent: React.FC<FareEstimateFormProps> = ({
  category,
  city_context,
  auth_data,
  on_lead_success_handler,
}) => {
  const { country, configs } = useLocaleContext();
  const content = useFareEstimateContent();
  const fe = content.fare_estimate_content;
  const dynamicSchema = makeSchema(
    country.mobileRegex,
    country.consentForCallback,
  );
  const restrictions = country.countryCode;

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    setError,
    watch,
    trigger,
  } = useForm<FormData>({
    mode: 'onBlur',
    resolver: zodResolver(dynamicSchema),
    defaultValues: getInitialValues(auth_data),
  });
  const requirementValue = watch('requirement');
  const [formError, setFormError] = React.useState<string | null>(null);
  const { locale } = useRouter();

  React.useEffect(() => {
    prefillAuthData(setValue, auth_data);
  }, [auth_data]);

  const onSubmit = (data: FormData) => {
    let callback = (error: string) => {
      setFormError(error);
    };
    submitFareEstimateFormData(
      data,
      setError,
      callback,
      stringToFormType(category),
      city_context?.category,
      auth_data?.isAuthenticated,
      getIsBookingFlowEnabled(configs),
      on_lead_success_handler,
    );
  };

  return (
    <CityContextProvider
      value={
        city_context ?? {
          citySelected: undefined,
          selectedCityLabel: undefined,
          setCitySelected: () => {},
          category: 'Home',
          queryParams: '',
        }
      }
    >
      <div
        id="fare-estimate-component"
        className="uppercase font-bold flex w-full bg-[#ffffff]"
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full"
          autoComplete="off"
        >
          <GoogleMapsAutoComplete
            identifiers={{ id: 'pickup' }}
            inputOptions={{
              placeholder: fe.field_params.pickup.placeholder,
              error: errors.pickup ? fe.field_params.pickup.error_msg : undefined,
            }}
            onLocationSelect={addressDetails => {
              setValue(
                'pickup',
                {
                  landmark: addressDetails.primaryAddress.landmark,
                  location: addressDetails.primaryAddress.location,
                },
                { shouldValidate: true },
              );
            }}
            restrictions={{ country: restrictions }}
            icons={{
              leftIcon: (
                <Image
                  src={fe.field_params.pickup.icon.url}
                  height={16}
                  width={16}
                  alt={fe.field_params.pickup.icon.alt}
                />
              ),
            }}
            className="mt-4 mb-7"
          />
          <GoogleMapsAutoComplete
            identifiers={{ id: 'drop' }}
            inputOptions={{
              placeholder: fe.field_params.drop.placeholder,
              error: errors.drop ? fe.field_params.drop.error_msg : undefined,
            }}
            onLocationSelect={addressDetails => {
              setValue(
                'drop',
                {
                  landmark: addressDetails.primaryAddress.landmark,
                  location: addressDetails.primaryAddress.location,
                },
                { shouldValidate: true },
              );
            }}
            restrictions={{ country: restrictions }}
            icons={{
              leftIcon: (
                <Image
                  src={fe.field_params.drop.icon.url}
                  height={16}
                  width={16}
                  alt={fe.field_params.drop.icon.alt}
                />
              ),
            }}
            className="my-7"
          />
          {!auth_data?.isAuthenticated ? (
            <TextInput
              identifiers={{ id: 'mobile' }}
              inputOptions={{
                placeholder: fe.field_params.phone.placeholder,
                error: errors.mobile ? fe.field_params.phone.error_msg : undefined,
              }}
              className="my-7"
              icons={{
                leftIcon: (
                  <Image
                    src={fe.field_params.phone.icon.url}
                    height={16}
                    width={16}
                    alt={fe.field_params.phone.icon.alt}
                  />
                ),
              }}
              {...register('mobile', {
                onChange: _e => {
                  if (errors.mobile) {
                    trigger('mobile');
                  }
                },
              })}
            />
          ) : null}
          {!auth_data?.isAuthenticated ? (
            <TextInput
              identifiers={{ id: 'name' }}
              inputOptions={{
                placeholder: fe.field_params.name.placeholder,
                error: errors.name ? fe.field_params.name.error_msg : undefined,
              }}
              className="my-7"
              icons={{
                leftIcon: (
                  <Image
                    src={fe.field_params.name.icon.url}
                    height={16}
                    width={16}
                    alt={fe.field_params.name.icon.alt}
                  />
                ),
              }}
              {...register('name', {
                onChange: _e => {
                  if (errors.name) {
                    trigger('name');
                  }
                },
              })}
            />
          ) : null}
          {!auth_data?.isAuthenticated ? (
            <RadioGroup
              identifiers={{ id: 'requirement' }}
              error={
                errors.requirement
                  ? fe.field_params.requirement.error_msg
                  : undefined
              }
              interactions={{
                onClick: value => {
                  setValue('requirement', value as 'personal' | 'business', {
                    shouldValidate: true,
                  });
                },
              }}
              radioOptions={[
                {
                  checked: requirementValue === FareEstimateFormLabel.personal,
                  disabled: false,
                  identifiers: { id: 'personal' },
                  label: fe.personal_use_label,
                  value: FareEstimateFormLabel.personal,
                  className: 'py-5 my-7',
                },
                {
                  checked: requirementValue === FareEstimateFormLabel.business,
                  disabled: false,
                  identifiers: { id: 'business' },
                  label: fe.business_use_label,
                  value: FareEstimateFormLabel.business,
                  className: 'py-5 my-7',
                },
              ]}
            />
          ) : null}
          <CallbackConsentCheckbox
            label={fe.consent_label}
            form={{ register, trigger }}
            class_name="items-start mt-[0.4rem]"
            should_show={
              (!auth_data?.isAuthenticated &&
                country.consentForCallback?.shouldShow) ??
              false
            }
          />
          <Button
            identifiers={{ id: 'submit-button' }}
            className={`w-full my-7 text-[#ffffff] transition-colors duration-300 ${
              isValid ? 'bg-[#0041f5]' : 'bg-[rgba(51,51,51,.24)]'
            }`}
            disabled={!isValid}
          >
            {fe.get_estimate_label}
          </Button>
          {formError && (
            <p className="text-red-500" data-testid={'text-input-error-mobile'}>
              {formError}
            </p>
          )}
        </form>
      </div>
    </CityContextProvider>
  );
};

export default FareEstimateFormCmp;
